/**
 * @file DeliveryBlockHeader.tsx
 * @description Шапка вкладки «Доставка»: состояние, происхождение условий,
 * сводка заявки и объясняющая полоса (phase19.24 шаг 5.2, §3).
 *
 * Отделено от блока: блок занимается действиями и раскладкой, здесь — то, что
 * менеджер читает глазами в первые пять секунд («что, почему, кто, когда»).
 *
 * Сводку заявки печатаем ТОЛЬКО при закрытой форме: рядом с открытой формой
 * она давала бы второй итог (сохранённый) под первым (живым из полей), и две
 * суммы на экране читались бы как ошибка.
 */

import type {
  DeliveryAgreementDto,
  OrderDeliveryReceiptDto,
} from "../../../lib/api-delivery-agreement";
import type { DeliveryBlockStateMeta } from "../../../lib/delivery-block-state";
import { Alert } from "../../ui/alert";
import { Badge } from "../../ui/badge";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { SectionLabel } from "../../ui/SectionLabel";
import { DeliveryAgreementSummary } from "../DeliveryAgreementSummary";
import { DeliverySourceNote } from "./DeliverySourceNote";

interface Props {
  meta: DeliveryBlockStateMeta;
  /**
   * Подпись бейджа из `deliveryBlockBadge`: при стадийном запрете она берётся
   * из стадии заказа, а не из записи состояния.
   */
  badge: string | null;
  /** Объясняющая строка из `deliveryBlockText` (стадия уточняет и её). */
  text: string;
  receipt: OrderDeliveryReceiptDto | null;
  agreement: DeliveryAgreementDto | null;
  /** Заказ завели на витрине. */
  siteOrder: boolean;
  /** Ответ о доставке не пришёл — условия читаются по заявке. */
  loadError: string | null;
}

export function DeliveryBlockHeader({
  meta,
  badge,
  text,
  receipt,
  agreement,
  siteOrder,
  loadError,
}: Props) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <SectionLabel as="h2">Доставка</SectionLabel>
        {badge ? <Badge variant={meta.tone}>{badge}</Badge> : null}
      </div>

      <DeliverySourceNote receipt={receipt} siteOrder={siteOrder} />

      {loadError ? (
        <Alert severity="warning" title="Доставка заказа загрузилась не полностью">
          {loadError} Часть условий показана по заявке — перезагрузите страницу,
          прежде чем менять их.
        </Alert>
      ) : null}

      {agreement && !meta.form ? (
        <DeliveryAgreementSummary
          agreement={agreement}
          // Ссылку подтверждения диктует менеджер заказов: роли без этого
          // права сервер отдаёт `confirmUrl: null`, и сводка не должна
          // полагаться на то, что null доехал (ревью 5.2).
          confirmLink={meta.confirmLink !== false}
        />
      ) : null}

      <NoticeStrip tone={noticeTone(meta.tone)}>{text}</NoticeStrip>
    </>
  );
}

/** Тон бейджа → тон полосы-объяснения (у полосы тонов три). */
function noticeTone(tone: DeliveryBlockStateMeta["tone"]): "info" | "warning" | "error" {
  if (tone === "destructive") return "error";
  if (tone === "warning") return "warning";
  return "info";
}
